import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { featuredGuides } from '@/mocks/guides';
import { categoryImages } from '@/mocks/categoryImages';

export function CategoryGrid() {
  return (
    <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50" id="categories">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
          Browse Product Categories
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center max-w-xl mx-auto mb-10">
          Explore our curated product categories powered by the main OOPBuy platform. Discover organized selections across fashion, accessories, and lifestyle essentials.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group block bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all duration-200 cursor-pointer overflow-hidden"
            >
              <div className="w-full h-32 md:h-36 overflow-hidden">
                <img
                  src={categoryImages[cat.name] || categoryImages['Bags']}
                  alt={cat.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="flex items-center justify-between px-3 py-3">
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
                <div className="w-4 h-4 flex items-center justify-center ml-1.5">
                  <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-500 text-sm transition-colors"></i>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ComparisonTable() {
  return (
    <section className="py-14 md:py-20 px-6 md:px-10 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
          Why OOPBuy Spreadsheet Is Becoming Essential
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center max-w-xl mx-auto mb-10">
          Modern users rely on structured spreadsheets because manual tracking is no longer efficient. Compare the key advantages below.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-background-50 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-primary-50">
                <th className="text-left px-5 py-3.5 text-sm font-semibold text-foreground-900 whitespace-nowrap">Feature</th>
                <th className="text-left px-5 py-3.5 text-sm font-semibold text-primary-600 whitespace-nowrap">
                  <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                    <i className="ri-star-fill text-primary-500 text-xs"></i>
                  </div>
                  OOPBuy Spreadsheet
                </th>
                <th className="text-left px-5 py-3.5 text-sm font-semibold text-foreground-700 whitespace-nowrap">Manual Spreadsheet</th>
                <th className="text-left px-5 py-3.5 text-sm font-semibold text-foreground-700 whitespace-nowrap">Basic Tools</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: 'Speed', oopbuy: 'High', manual: 'Low', basic: 'Medium' },
                { feature: 'Accuracy', oopbuy: 'High', manual: 'Medium', basic: 'Low' },
                { feature: 'Automation', oopbuy: 'Yes', manual: 'No', basic: 'Limited' },
                { feature: 'Scalability', oopbuy: 'High', manual: 'Low', basic: 'Low' },
                { feature: 'Best For', oopbuy: 'Modern Buyers', manual: 'Small Tasks', basic: 'Beginners' },
              ].map((row, i) => (
                <tr key={row.feature} className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                  <td className="px-5 py-3 text-sm font-medium text-foreground-800 whitespace-nowrap">{row.feature}</td>
                  <td className="px-5 py-3 text-sm text-primary-600 font-semibold whitespace-nowrap">{row.oopbuy}</td>
                  <td className="px-5 py-3 text-sm text-foreground-600 whitespace-nowrap">{row.manual}</td>
                  <td className="px-5 py-3 text-sm text-foreground-600 whitespace-nowrap">{row.basic}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
          <Link
            to="/guide/oopbuy-spreadsheet-complete-guide"
            className="px-6 py-2.5 bg-secondary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-md hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Read Full Comparison
          </Link>
        </div>
      </div>
    </section>
  );
}

export function DifferentSection() {
  return (
    <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
          A Centralized Knowledge System for Spreadsheet Buyers
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center max-w-xl mx-auto mb-10">
          Unlike random blogs, this website is structured as a topical authority hub focused entirely on OOPBuy Spreadsheet systems.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {[
            { icon: 'ri-file-search-line', title: 'Deep Comparison Content', desc: 'In-depth analyses comparing spreadsheet systems, tools, and methodologies for modern buying workflows.' },
            { icon: 'ri-lightbulb-line', title: 'Real-World Use Cases', desc: 'Practical examples and scenarios showing how OOPBuy Spreadsheet systems improve real purchasing decisions.' },
            { icon: 'ri-graduation-cap-line', title: 'Beginner to Advanced Tutorials', desc: 'Structured learning paths from fundamental concepts to advanced automation strategies for every skill level.' },
            { icon: 'ri-flow-chart', title: 'Decision-Making Frameworks', desc: 'Proven frameworks that help you evaluate options systematically using data-driven spreadsheet methodologies.' },
          ].map((item) => (
            <div key={item.title} className="flex gap-4 p-5 bg-background-50 border border-background-200/70 rounded-lg hover:border-accent-300 transition-colors">
              <div className="w-10 h-10 flex-shrink-0 rounded-md bg-accent-100 flex items-center justify-center">
                <i className={`${item.icon} text-accent-600 text-lg`}></i>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-foreground-500 text-center mt-6 italic">
          Every guide is designed to lead users toward better understanding and actionable insights.
        </p>
      </div>
    </section>
  );
}

export function MainPlatformSection() {
  return (
    <section className="py-14 md:py-20 px-6 md:px-10 bg-accent-50/50">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
          Get Full Access to Advanced OOPBuy Spreadsheet Tools
        </h2>
        <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto mb-8">
          While this website provides educational content, advanced users may require full system access.
          For complete functionality, automation features, and advanced spreadsheet systems:
        </p>
        <a
          href="https://kaspreadsheet.com/"
          target="_blank"
          rel="noopener nofollow"
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-base font-semibold rounded-md hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-rocket-2-line text-lg"></i>
          </div>
          Visit the Main OOPBuy Platform
        </a>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10 max-w-lg mx-auto text-left">
          {[
            'Unlock full spreadsheet automation',
            'Access advanced tracking systems',
            'Improve buying efficiency at scale',
            'Use structured decision frameworks',
          ].map((benefit) => (
            <div key={benefit} className="flex items-start gap-2">
              <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                <i className="ri-checkbox-circle-fill text-accent-500 text-sm"></i>
              </div>
              <span className="text-sm text-foreground-700">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeaturedGuidesSection() {
  return (
    <section className="py-14 md:py-20 px-6 md:px-10 bg-background-100">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
          Start Learning OOPBuy Spreadsheet Systems
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center max-w-xl mx-auto mb-10">
          Explore our complete library of guides. Each guide includes detailed explanations, comparisons, and real-world applications to help you master OOPBuy Spreadsheet.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {featuredGuides.map((guide, i) => (
            <Link
              key={guide.title}
              to={guide.slug}
              className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
            >
              <div className="w-full h-36 md:h-40 overflow-hidden">
                <img
                  src={guide.image}
                  alt={guide.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5">
                <div className="w-9 h-9 rounded-md bg-primary-100 flex items-center justify-center mb-3 group-hover:bg-primary-200 transition-colors">
                  <span className="text-primary-600 font-heading text-sm font-bold">{i + 1}</span>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {guide.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{guide.description}</p>
                <div className="flex items-center gap-1 mt-3 text-primary-500 text-sm font-medium">
                  Read Guide
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TrustSection() {
  return (
    <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
          Transparency &amp; Safety Information
        </h2>
        <p className="text-sm md:text-base text-foreground-600 text-center max-w-xl mx-auto mb-8">
          This website is an informational resource only. We do NOT process transactions or provide financial services.
        </p>
        <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6">
          <p className="text-sm text-foreground-700 leading-relaxed mb-4">
            Users should always:
          </p>
          <ul className="space-y-2">
            {[
              'Verify third-party platforms independently',
              'Review external tools before making decisions',
              'Understand risks associated with data-driven tools',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-foreground-600">
                <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <i className="ri-information-line text-secondary-500 text-sm"></i>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-background-50 rounded-lg overflow-hidden text-sm">
            <thead>
              <tr className="bg-secondary-100">
                <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-900 whitespace-nowrap">Risk Type</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-900 whitespace-nowrap">Description</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-900 whitespace-nowrap">Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {[
                { type: 'Data Accuracy', desc: 'External data may change', recommendation: 'Always verify' },
                { type: 'Third-party Links', desc: 'Not controlled by us', recommendation: 'Use caution' },
                { type: 'Decision Risk', desc: 'Misinterpretation of data', recommendation: 'Cross-check sources' },
              ].map((row, i) => (
                <tr key={row.type} className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                  <td className="px-4 py-2.5 font-medium text-foreground-800 whitespace-nowrap">{row.type}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row.desc}</td>
                  <td className="px-4 py-2.5 text-accent-600 font-medium whitespace-nowrap">{row.recommendation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className="py-16 md:py-24 px-6 md:px-10 bg-background-100">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
          Start Exploring OOPBuy Spreadsheet Intelligence
        </h2>
        <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto mb-8">
          Whether you are a beginner or advanced user, this hub gives you the foundation to understand and use spreadsheet systems more effectively.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
          <Link
            to="/guide/oopbuy-spreadsheet-complete-guide"
            className="px-6 py-3 bg-secondary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-md hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Explore All Guides
          </Link>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Go to Main Platform
          </a>
          <a
            href="#categories"
            className="px-6 py-3 bg-background-50 border border-background-200/70 text-foreground-700 text-sm font-semibold rounded-md hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
          >
            Compare Categories
          </a>
        </div>
      </div>
    </section>
  );
}